export type TDashboardEventType = "league" | "tournament" | "shotgun";

export interface IDashboardEvent {
  event_id: number;
  event_name: string;
  event_type: TDashboardEventType | "group_booking";
  start_time: string;
  end_time: string;
  facility_id?: number;
  course_id?: number;
}

export interface IEventBand {
  id: string;
  eventName: string;
  eventType: TDashboardEventType;
  startTime: string;
  endTime: string;
  leftPercent: number;
  widthPercent: number;
  row: number;
  color: string;
  fillColor: string;
}

export const EVENT_TYPE_COLORS: Record<TDashboardEventType, string> = {
  league: "#7357D6",
  tournament: "#FEAB2D",
  shotgun: "#F73952",
};

export const EVENT_TYPE_FILL_COLORS: Record<TDashboardEventType, string> = {
  league: "rgba(115, 87, 214, 0.14)",
  tournament: "rgba(254, 171, 45, 0.14)",
  shotgun: "rgba(247, 57, 82, 0.14)",
};

export type TEventTypeIconName = "faUsers" | "faTrophy";

export const EVENT_TYPE_ICON_NAMES: Record<TDashboardEventType, TEventTypeIconName | null> = {
  league: "faUsers",
  tournament: "faTrophy",
  shotgun: null,
};

const DEFAULT_AXIS_START = 6;
const DEFAULT_AXIS_END = 19;
export const LABEL_ROW_HEIGHT = 30;
export const MIN_LABEL_WIDTH_FOR_NAME_WITH_ICON = 56;

export function filterTimelineEvents(events?: Array<IDashboardEvent>): Array<IDashboardEvent> {
  if (!events?.length) {
    return [];
  }

  return events.filter(
    event => event.event_type === "league" || event.event_type === "tournament" || event.event_type === "shotgun",
  );
}

/** Hour of day from "HH:mm:ss" (minutes ignored — bands snap to hour columns). */
function getHourFromTime(time?: string): number | null {
  if (!time) {
    return null;
  }

  const hour = Number(time.split(":")[0]);
  return Number.isNaN(hour) ? null : hour;
}

export function hourLabelFromStartHour(startHour: number): string {
  return `${startHour} - ${startHour + 1}`;
}

export function parseHourLabelStartHour(hourLabel: string): number {
  return Number(hourLabel.split("-")[0]?.trim());
}

function mergeEventSegments(events: Array<IDashboardEvent>): Array<IDashboardEvent> {
  const sorted = [...events].sort((a, b) => a.start_time.localeCompare(b.start_time));
  const merged: IDashboardEvent[] = [];

  sorted.forEach(event => {
    const previous = merged[merged.length - 1];

    if (previous && previous.event_id === event.event_id && previous.event_type === event.event_type) {
      if (event.end_time > previous.end_time) {
        previous.end_time = event.end_time;
      }
      if (event.start_time < previous.start_time) {
        previous.start_time = event.start_time;
      }
      return;
    }

    merged.push({ ...event });
  });

  return merged;
}

export function buildHourAxis(
  teetimes: Array<{ start_time?: string }>,
  events: Array<IDashboardEvent>,
): string[] {
  const hourSet = new Set<number>();

  teetimes?.forEach(teeTime => {
    const hour = getHourFromTime(teeTime.start_time);
    if (hour !== null) {
      hourSet.add(hour);
    }
  });

  events?.forEach(event => {
    const startHour = getHourFromTime(event.start_time);
    const endHour = getHourFromTime(event.end_time);
    if (startHour === null || endHour === null) {
      return;
    }

    for (let hour = startHour; hour <= Math.max(endHour, startHour); hour++) {
      hourSet.add(hour);
    }
  });

  if (hourSet.size === 0) {
    return Array.from({ length: DEFAULT_AXIS_END - DEFAULT_AXIS_START }, (_, index) =>
      hourLabelFromStartHour(DEFAULT_AXIS_START + index),
    );
  }

  const sortedHours = Array.from(hourSet).sort((a, b) => a - b);
  const minHour = Math.min(sortedHours[0], DEFAULT_AXIS_START);
  const maxHour = Math.max(sortedHours[sortedHours.length - 1], DEFAULT_AXIS_END - 1);

  const hourLabels: string[] = [];
  for (let hour = minHour; hour <= maxHour; hour++) {
    hourLabels.push(hourLabelFromStartHour(hour));
  }

  return hourLabels;
}

function assignLabelRows(
  bands: Array<{ leftPercent: number; widthPercent: number; row: number }>,
): Array<{ leftPercent: number; widthPercent: number; row: number }> {
  const rowEndPositions: number[] = [];

  return bands.map(band => {
    const bandStart = band.leftPercent;
    const bandEnd = band.leftPercent + band.widthPercent;

    let assignedRow = rowEndPositions.findIndex(rowEnd => rowEnd <= bandStart);
    if (assignedRow === -1) {
      assignedRow = rowEndPositions.length;
      rowEndPositions.push(bandEnd);
    } else {
      rowEndPositions[assignedRow] = bandEnd;
    }

    return {
      ...band,
      row: assignedRow,
    };
  });
}

function columnIndexForHour(hourLabels: string[], hour: number): number {
  return hourLabels.findIndex(label => parseHourLabelStartHour(label) === hour);
}

/**
 * Build band spans in hour-column units (0 .. hourLabels.length).
 * Half values (e.g. 3.5) are used when two events share a column.
 */
function computeBandColumnSpans(
  hourLabels: string[],
  events: Array<IDashboardEvent>,
): Array<{
  event: IDashboardEvent;
  startIndex: number;
  endIndex: number;
}> {
  if (!hourLabels.length || !events.length) {
    return [];
  }

  const spans = mergeEventSegments(events)
    .map(event => {
      const startHour = getHourFromTime(event.start_time);
      const endHour = getHourFromTime(event.end_time);

      if (startHour === null || endHour === null) {
        return null;
      }

      const startIndex = columnIndexForHour(hourLabels, startHour);
      const endColumn = columnIndexForHour(hourLabels, Math.max(endHour, startHour));

      if (startIndex === -1 || endColumn === -1) {
        return null;
      }

      return {
        event,
        startIndex,
        endIndex: endColumn + 1,
      };
    })
    .filter((span): span is { event: IDashboardEvent; startIndex: number; endIndex: number } => span !== null)
    .sort((a, b) => a.startIndex - b.startIndex || a.endIndex - b.endIndex);

  for (let index = 0; index < spans.length - 1; index++) {
    const current = spans[index];
    const next = spans[index + 1];
    const currentEndHour = getHourFromTime(current.event.end_time);
    const nextStartHour = getHourFromTime(next.event.start_time);

    if (currentEndHour !== null && currentEndHour === nextStartHour) {
      const sharedColumn = columnIndexForHour(hourLabels, currentEndHour);
      if (sharedColumn === -1) {
        continue;
      }

      const midpoint = sharedColumn + 0.5;
      current.endIndex = midpoint;
      next.startIndex = midpoint;
    }
  }

  return spans.filter(span => span.endIndex > span.startIndex);
}

export function computeEventBands(hourLabels: string[], events: Array<IDashboardEvent>): IEventBand[] {
  const columnCount = hourLabels.length;
  if (!columnCount) {
    return [];
  }

  const columnWidth = 100 / columnCount;

  const rawBands = computeBandColumnSpans(hourLabels, events).map(span => {
    const eventType = span.event.event_type as TDashboardEventType;

    return {
      id: `${span.event.event_type}-${span.event.event_id}-${span.event.start_time}`,
      eventName: span.event.event_name,
      eventType,
      startTime: span.event.start_time,
      endTime: span.event.end_time,
      leftPercent: span.startIndex * columnWidth,
      widthPercent: (span.endIndex - span.startIndex) * columnWidth,
      row: 0,
      color: EVENT_TYPE_COLORS[eventType],
      fillColor: EVENT_TYPE_FILL_COLORS[eventType],
    };
  });

  return assignLabelRows(rawBands) as IEventBand[];
}

export interface IGraphHourBooking {
  entityId: number;
  bookings: number;
}

export interface IGraphHourData {
  hour: string;
  bookingsPerEntity: IGraphHourBooking[];
}

interface ITeeTimeSlot {
  course_id?: number;
  facility_id?: number;
  tee_time_slot_status?: string;
}

interface ITeeTimeWithSlots {
  start_time?: string;
  slots?: ITeeTimeSlot[];
}

export function buildGraphHourData(
  hourLabels: string[],
  teetimes: ITeeTimeWithSlots[],
  entityIds: number[],
  entityKey: "course_id" | "facility_id",
): IGraphHourData[] {
  const groupedTeeTimeHours: Record<string, ITeeTimeWithSlots[]> = {};

  teetimes?.forEach(teeTime => {
    const hour = getHourFromTime(teeTime.start_time);
    if (hour === null) {
      return;
    }

    const hourLabel = hourLabelFromStartHour(hour);
    if (!groupedTeeTimeHours[hourLabel]) {
      groupedTeeTimeHours[hourLabel] = [];
    }
    groupedTeeTimeHours[hourLabel].push(teeTime);
  });

  return hourLabels.map(hourLabel => {
    const teeTimesForHour = groupedTeeTimeHours[hourLabel] ?? [];

    const bookingsPerEntity = entityIds.map(entityId => {
      const bookings = teeTimesForHour.reduce((acc, teeTime) => {
        const count =
          teeTime.slots?.filter(
            slot => slot?.[entityKey] === entityId && slot?.tee_time_slot_status === "booked",
          ).length || 0;
        return acc + count;
      }, 0);

      return { entityId, bookings };
    });

    return {
      hour: hourLabel,
      bookingsPerEntity,
    };
  });
}
